import {
  cutPart,
  partMaxHeight,
  rowInk,
} from '@/domain/menu-import/tall-pages';
import {
  canvasToJpeg,
  context2d,
  createCanvas,
  releaseCanvas,
} from './capture-dom';

/** One part of a tall page, ready to upload like a page. */
export interface ITallPart {
  blob: Blob;
  width: number;
  height: number;
  /** 1-based; null when the whole page fits in one part (it is then a whole page). */
  part: number | null;
}

/** A tall page that can draw any window of itself at its final width. */
export interface ITallSource {
  width: number;
  height: number;
  /** Draws rows `top` to `top + canvas.height` of the page onto `canvas` (white behind). */
  drawWindow(canvas: HTMLCanvasElement, top: number): Promise<void>;
}

export interface ITallSplitResult {
  parts: number;
  /** The page needed more than `maxParts` parts: the rest is left out. */
  truncated: boolean;
}

/**
 * Splits a tall page into parts one window at a time, so no canvas is ever
 * taller than a part (iOS caps the canvas area): each window is drawn, its
 * row ink measured, the part cut on a quiet band and encoded, and handed to
 * `onPart` before the next window is drawn. Stops after `maxParts`. Errors
 * thrown by `onPart` propagate unchanged.
 */
export async function splitTallPage(
  source: ITallSource,
  quality: number,
  onPart: (part: ITallPart) => Promise<void>,
  maxParts: number
): Promise<ITallSplitResult> {
  const maxHeight = partMaxHeight(source.width);
  let top: number | null = 0;
  let part = 0;
  while (top !== null) {
    if (part >= maxParts) return { parts: part, truncated: true };
    part += 1;
    const windowHeight = Math.min(maxHeight, source.height - top);
    let windowCanvas: HTMLCanvasElement | null = null;
    let partCanvas: HTMLCanvasElement | null = null;
    try {
      windowCanvas = createCanvas(source.width, windowHeight);
      await source.drawWindow(windowCanvas, top);
      const pixels = context2d(windowCanvas).getImageData(
        0,
        0,
        source.width,
        windowHeight
      );
      const cut = cutPart(
        rowInk(pixels.data, source.width, windowHeight),
        top,
        source.height,
        maxHeight
      );
      const height = cut.bottom - top;
      if (height === windowHeight) {
        partCanvas = windowCanvas;
        windowCanvas = null;
      } else {
        partCanvas = createCanvas(source.width, height);
        context2d(partCanvas).drawImage(windowCanvas, 0, 0);
      }
      const blob = await canvasToJpeg(partCanvas, quality);
      await onPart({
        blob,
        width: source.width,
        height,
        part: part === 1 && cut.nextTop === null ? null : part,
      });
      top = cut.nextTop;
    } finally {
      releaseCanvas(windowCanvas);
      releaseCanvas(partCanvas);
    }
  }
  return { parts: part, truncated: false };
}
